import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Pill, AlertTriangle, Activity, Brain, Users, Calendar,
    ChevronRight, Loader2, Sparkles, Stethoscope, ClipboardList,
    TrendingUp, TrendingDown, Minus, Droplet, Heart, AlertOctagon,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import ProblemList from "./ProblemList";
import ClinicalSummaryCard from "./ClinicalSummaryCard";
import { useAuth } from "@/contexts/AuthContext";

const CRITICAL_META = {
    ckd_epi: { label: "TFG (CKD-EPI)", icon: Droplet, unit: "mL/min", betterWhen: "up" },
    hba1c: { label: "HbA1c", icon: Droplet, unit: "%", betterWhen: "down" },
    score2_op: { label: "SCORE2-OP", icon: Heart, unit: "%", betterWhen: "down" },
    framingham: { label: "Framingham CVD", icon: Heart, unit: "%", betterWhen: "down" },
    ascvd: { label: "ASCVD", icon: Heart, unit: "%", betterWhen: "down" },
};

export default function SnapshotHub({ patientId, onOpenTab }) {
    const [snapshot, setSnapshot] = useState(null);
    const [risk, setRisk] = useState(null);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();
    const canGenerateSummary = user && (user.role === "doctor" || user.role === "admin");

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api.get(`/patients/${patientId}/snapshot`),
            api.get(`/patients/${patientId}/risk-profile`).catch(() => ({ data: null })),
        ])
            .then(([snap, prof]) => {
                setSnapshot(snap.data);
                setRisk(prof.data);
            })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [patientId]);

    if (loading) {
        return (
            <div className="card-earthy flex items-center gap-2 text-sm text-muted-clinical">
                <Loader2 className="animate-spin" size={14} /> Carregando resumo...
            </div>
        );
    }
    if (!snapshot) return null;

    const { problems, medications, risks, npi_totals, header } = snapshot;
    const alerts = (risk?.alerts || []);
    const criticalDeltas = risk?.critical_deltas || {};

    // 3 escalas críticas, na ordem: TFG, HbA1c, SCORE2-OP (fallback Framingham→ASCVD)
    const criticalKeys = ["ckd_epi", "hba1c"];
    if (criticalDeltas.score2_op) criticalKeys.push("score2_op");
    else if (criticalDeltas.framingham) criticalKeys.push("framingham");
    else if (criticalDeltas.ascvd) criticalKeys.push("ascvd");

    return (
        <div className="space-y-4" data-testid="snapshot-hub">
            {/* Resumo clínico narrativo (IA) + chips estruturados */}
            <ClinicalSummaryCard patientId={patientId} canGenerate={canGenerateSummary} />

            {/* Alertas por calculadora */}
            {alerts.length > 0 && (
                <div className="card-earthy border-l-4 border-[#B33A3A] bg-[#FBEEEE]/40" data-testid="snapshot-alerts">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertOctagon size={18} className="text-[#B33A3A]" />
                        <h3 className="font-display text-base font-medium text-[#B33A3A]">
                            Alertas clínicos ({alerts.length})
                        </h3>
                    </div>
                    <ul className="space-y-2">
                        {alerts.map((a, i) => (
                            <li
                                key={i}
                                className="text-sm"
                                data-testid={`snapshot-alert-${a.type}`}
                            >
                                <div className="flex items-start gap-2">
                                    <span
                                        className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full shrink-0 mt-0.5"
                                        style={{
                                            background: a.severity === "critical" ? "#B33A3A" : "#8A6A15",
                                            color: "#FFF",
                                        }}
                                    >
                                        {a.severity === "critical" ? "Crítico" : "Atenção"}
                                    </span>
                                    <div>
                                        <p className="font-medium text-brand">{a.title}</p>
                                        <p className="text-xs text-brand-secondary">{a.detail}</p>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* 3 mini-cards das escalas críticas */}
            {criticalKeys.some((k) => criticalDeltas[k]) && (
                <div className="grid md:grid-cols-3 gap-3" data-testid="snapshot-critical-scores">
                    {criticalKeys.map((k) => {
                        const d = criticalDeltas[k];
                        if (!d) return null;
                        const meta = CRITICAL_META[k];
                        const Icon = meta.icon;
                        const arrow = d.direction === "worse" ? TrendingDown : d.direction === "better" ? TrendingUp : Minus;
                        const ArrowIcon = arrow;
                        const worseColor = d.direction === "worse" ? "#B33A3A" : d.direction === "better" ? "#4A6B58" : "#8B8B8B";
                        return (
                            <div key={k} className="card-earthy" data-testid={`snapshot-critical-${k}`}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs uppercase tracking-widest text-brand-secondary font-semibold flex items-center gap-1">
                                        <Icon size={12} /> {meta.label}
                                    </span>
                                    <button onClick={() => onOpenTab?.("calculadoras")} className="text-xs text-brand hover:underline">
                                        <ChevronRight size={11} />
                                    </button>
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <span className="font-display text-3xl font-medium text-brand">{d.value}</span>
                                    <span className="text-xs text-muted-clinical">{d.unit}</span>
                                    {d.band && (
                                        <span className="ml-auto text-[10px] uppercase tracking-widest font-semibold text-brand-secondary">
                                            {d.band}
                                        </span>
                                    )}
                                </div>
                                {d.previous_value != null && d.delta != null && (
                                    <p className="text-xs flex items-center gap-1 mt-1" style={{ color: worseColor }}>
                                        <ArrowIcon size={12} />
                                        {d.delta > 0 ? "+" : ""}{d.delta} vs {d.previous_value}
                                        <span className="text-muted-clinical">
                                            ({d.direction === "worse" ? "piorou" : d.direction === "better" ? "melhorou" : "estável"})
                                        </span>
                                    </p>
                                )}
                                {d.previous_value == null && (
                                    <p className="text-[11px] text-muted-clinical mt-1 italic">Primeira medição</p>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-3">
                {/* 1. Problemas Ativos */}
                <div className="card-earthy lg:col-span-2" data-testid="snapshot-card-problems">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display text-base font-medium text-brand flex items-center gap-2">
                            <ClipboardList size={16} /> Problemas Ativos
                        </h3>
                        <button
                            onClick={() => onOpenTab?.("problemas")}
                            className="text-xs text-brand hover:underline flex items-center gap-0.5"
                        >
                            Gerenciar <ChevronRight size={11} />
                        </button>
                    </div>
                    {problems.length === 0 ? (
                        <p className="text-sm text-muted-clinical italic">
                            Nenhum problema ativo cadastrado.
                        </p>
                    ) : (
                        <ul className="space-y-1.5">
                            {problems.slice(0, 6).map((p, i) => (
                                <li key={p.id || i} className="flex items-start gap-2 text-sm" data-testid={`snapshot-problem-${i}`}>
                                    <span
                                        className="w-2 h-2 rounded-full mt-1.5 shrink-0"
                                        style={{
                                            background: p.priority === "high" ? "#B33A3A" : p.priority === "low" ? "#4A6B58" : "#8A6A15",
                                        }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-brand font-medium">{p.title}</p>
                                        {(p.cid10 || p.description) && (
                                            <p className="text-xs text-muted-clinical">
                                                {p.cid10 && <span className="font-mono mr-2">{p.cid10}</span>}
                                                {p.description}
                                            </p>
                                        )}
                                    </div>
                                    {p.source === "care_plan" && (
                                        <span className="text-[10px] px-1.5 py-0.5 bg-[#EDE4FF] text-[#5B3F72] rounded-full font-semibold shrink-0">
                                            Do plano
                                        </span>
                                    )}
                                </li>
                            ))}
                            {problems.length > 6 && (
                                <li className="text-xs text-muted-clinical italic pl-4">
                                    + {problems.length - 6} outros
                                </li>
                            )}
                        </ul>
                    )}
                </div>

                {/* 2. Alergias */}
                <div className="card-earthy border-l-4 border-[#B33A3A]" data-testid="snapshot-card-allergies">
                    <h3 className="font-display text-base font-medium text-[#B33A3A] flex items-center gap-2 mb-3">
                        <AlertTriangle size={16} /> Alergias
                    </h3>
                    {header.allergies ? (
                        <p className="text-sm text-brand whitespace-pre-wrap">{header.allergies}</p>
                    ) : (
                        <p className="text-sm text-muted-clinical italic">
                            Nenhuma alergia registrada. Confirme na próxima anamnese.
                        </p>
                    )}
                </div>

                {/* 3. Medicações em uso */}
                <div className="card-earthy lg:col-span-2" data-testid="snapshot-card-meds">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display text-base font-medium text-brand flex items-center gap-2">
                            <Pill size={16} /> Medicações em uso
                            {medications.length > 0 && <span className="text-xs text-muted-clinical">({medications.length})</span>}
                        </h3>
                        <button
                            onClick={() => onOpenTab?.("medicacoes")}
                            className="text-xs text-brand hover:underline flex items-center gap-0.5"
                        >
                            Detalhes <ChevronRight size={11} />
                        </button>
                    </div>
                    {medications.length === 0 ? (
                        <p className="text-sm text-muted-clinical italic">
                            Nenhuma medicação registrada.
                        </p>
                    ) : (
                        <ul className="grid md:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                            {medications.slice(0, 8).map((m, i) => (
                                <li key={i} className="flex items-baseline gap-2" data-testid={`snapshot-med-${i}`}>
                                    <span className="text-brand-secondary">•</span>
                                    <div>
                                        <span className="font-medium text-brand">{m.name || m.nome || "—"}</span>
                                        {(m.dose || m.dosage) && (
                                            <span className="text-xs text-muted-clinical ml-1">
                                                · {m.dose || m.dosage}
                                            </span>
                                        )}
                                    </div>
                                </li>
                            ))}
                            {medications.length > 8 && (
                                <li className="text-xs text-muted-clinical italic md:col-span-2">
                                    + {medications.length - 8} outras medicações
                                </li>
                            )}
                        </ul>
                    )}
                </div>

                {/* 4. Riscos */}
                <div className="card-earthy" data-testid="snapshot-card-risks">
                    <h3 className="font-display text-base font-medium text-brand flex items-center gap-2 mb-3">
                        <Brain size={16} /> Riscos & Escalas
                    </h3>
                    <RiskGrid risks={risks} npi={npi_totals} />
                </div>

                {/* 5. Última consulta */}
                <div className="card-earthy" data-testid="snapshot-card-last-consult">
                    <h3 className="font-display text-base font-medium text-brand flex items-center gap-2 mb-3">
                        <Stethoscope size={16} /> Última consulta
                    </h3>
                    {header.last_consultation ? (
                        <div className="text-sm space-y-1">
                            <p className="text-brand-secondary">
                                <strong>
                                    {header.last_consultation.scheduled_at
                                        ? new Date(header.last_consultation.scheduled_at).toLocaleDateString("pt-BR")
                                        : header.last_consultation.created_at?.slice(0, 10)}
                                </strong>
                            </p>
                            <p className="text-muted-clinical text-xs">
                                {consultTypeLabel(header.last_consultation.consultation_type)}
                            </p>
                            <Link
                                to={`/consultations/${header.last_consultation.id}`}
                                className="text-brand hover:underline text-xs flex items-center gap-0.5 mt-2"
                            >
                                Abrir consulta <ChevronRight size={11} />
                            </Link>
                        </div>
                    ) : (
                        <p className="text-sm text-muted-clinical italic">
                            Nenhuma consulta registrada.
                        </p>
                    )}
                </div>

                {/* 6. Próxima consulta */}
                <div className="card-earthy" data-testid="snapshot-card-next-consult">
                    <h3 className="font-display text-base font-medium text-brand flex items-center gap-2 mb-3">
                        <Calendar size={16} /> Próxima consulta
                    </h3>
                    {header.next_consultation ? (
                        <div className="text-sm space-y-1">
                            <p className="text-brand-secondary">
                                <strong>
                                    {new Date(header.next_consultation.scheduled_at).toLocaleString("pt-BR", { day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}
                                </strong>
                            </p>
                            <p className="text-muted-clinical text-xs">
                                {consultTypeLabel(header.next_consultation.consultation_type)}
                            </p>
                            <Link
                                to={`/consultations/${header.next_consultation.id}`}
                                className="text-brand hover:underline text-xs flex items-center gap-0.5 mt-2"
                            >
                                Abrir <ChevronRight size={11} />
                            </Link>
                        </div>
                    ) : (
                        <p className="text-sm text-muted-clinical italic">
                            Nenhum retorno agendado. Considere agendar um seguimento.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

function consultTypeLabel(t) {
    const m = {
        ambulatorial: "Consulta Geriátrica Ambulatorial",
        domiciliar: "Acompanhamento Domiciliar",
        telemedicina_canabis: "Telemedicina — Canabis",
        retorno: "Consulta de Retorno",
    };
    return m[t] || t || "—";
}

function RiskGrid({ risks, npi }) {
    const items = [
        { key: "fried", label: "Fried", value: risks.fried, format: (v) => `${v}/5`, warn: (v) => v >= 3 },
        { key: "cfs", label: "CFS", value: risks.cfs, format: (v) => `${v}/9`, warn: (v) => v >= 5 },
        { key: "meem", label: "MEEM", value: risks.meem, format: (v) => `${v}/30`, warn: (v) => v < 24 },
        { key: "gds15", label: "GDS-15", value: risks.gds15, format: (v) => `${v}/15`, warn: (v) => v >= 5 },
        { key: "man", label: "MAN", value: risks.man, format: (v) => `${v}`, warn: (v) => v < 17 },
        { key: "zarit", label: "Zarit", value: risks.zarit, format: (v) => `${v}`, warn: (v) => v >= 20 },
    ];
    const withValue = items.filter((i) => i.value != null);
    const npiActive = npi && (npi.ativos > 0);

    if (withValue.length === 0 && !npiActive) {
        return (
            <p className="text-sm text-muted-clinical italic">
                Nenhuma escala preenchida ainda.
            </p>
        );
    }
    return (
        <div className="grid grid-cols-3 gap-2">
            {withValue.map((it) => (
                <div
                    key={it.key}
                    className={`rounded-md p-2 text-center ${it.warn(it.value) ? "bg-[#FBEEEE] border border-[#B33A3A]/30" : "bg-alt/60"}`}
                    data-testid={`snapshot-risk-${it.key}`}
                >
                    <p className={`text-xs uppercase tracking-widest font-semibold ${it.warn(it.value) ? "text-[#B33A3A]" : "text-brand-secondary"}`}>
                        {it.label}
                    </p>
                    <p className={`font-medium mt-0.5 ${it.warn(it.value) ? "text-[#B33A3A]" : "text-brand"}`}>
                        {it.format(it.value)}
                    </p>
                </div>
            ))}
            {npiActive && (
                <div className="col-span-3 rounded-md p-2 bg-[#FBF4E5] border border-[#8A6A15]/30" data-testid="snapshot-risk-npi">
                    <p className="text-xs uppercase tracking-widest font-semibold text-[#8A6A15]">NPI-Q</p>
                    <p className="text-brand text-sm">
                        {npi.ativos} domínios ativos · gravidade {npi.gravidade} · estresse cuidador {npi.estresse}
                    </p>
                </div>
            )}
        </div>
    );
}
